// ---------------------------------------------------------------------------
// SpecComplianceTab -- compact validation panel for DevTools bottom panel.
//
// Simplified version of ValidationTab: shows score badge + block-by-block
// issue list. No auto-fix, no element navigation, no cross-block section.
// ---------------------------------------------------------------------------

import { useState, useCallback, useMemo } from 'react'
import { useStudioStore } from '@/store/studio-store'
import { blockApi, type BlockBundle, type BlockValidateResponse, type BlockValidationIssue } from '@/services/block-api-service'
import type { LoadedApp } from '@/features/page-editor/store/types'

type ValidationState = 'idle' | 'running' | 'done' | 'error'
type LevelFilter = 'all' | 'error' | 'warning' | 'info'

function gradeColor(grade: string) {
  switch (grade) {
    case 'pass': return 'text-success'
    case 'warn': return 'text-yellow-400'
    case 'reject': return 'text-red-400'
    case 'partial': return 'text-amber-400'
    default: return 'text-muted-foreground'
  }
}

function levelIcon(level: string) {
  switch (level) {
    case 'error': return '\u25CF'
    case 'warning': return '\u25B2'
    case 'info': return '\u25CB'
    default: return '\u00B7'
  }
}

function levelColor(level: string) {
  switch (level) {
    case 'error': return 'text-red-400'
    case 'warning': return 'text-yellow-400'
    case 'info': return 'text-blue-400'
    default: return 'text-muted-foreground'
  }
}

function scoreColor(score: number | null): string {
  if (score === null) return 'text-muted-foreground'
  if (score >= 80) return 'text-success'
  if (score >= 60) return 'text-yellow-400'
  return 'text-red-400'
}

function scoreBgColor(score: number | null): string {
  if (score === null) return 'bg-muted/30'
  if (score >= 80) return 'bg-success/10'
  if (score >= 60) return 'bg-yellow-500/10'
  return 'bg-red-500/10'
}

export function SpecComplianceTab() {
  const currentProject = useStudioStore((s) => s.currentProject)
  const [state, setState] = useState<ValidationState>('idle')
  const [results, setResults] = useState<Record<string, BlockValidateResponse>>({})
  const [error, setError] = useState<string | null>(null)
  const [expandedBlock, setExpandedBlock] = useState<string | null>(null)
  const [levelFilter, setLevelFilter] = useState<LevelFilter>('all')
  const [stageFilter, setStageFilter] = useState<number | null>(null)

  // Summary stats
  const summary = useMemo(() => {
    const entries = Object.values(results)
    if (entries.length === 0) return null
    const pass = entries.filter((r) => r.grade === 'pass').length
    const warn = entries.filter((r) => r.grade === 'warn').length
    const reject = entries.filter((r) => r.grade === 'reject').length
    const partial = entries.filter((r) => r.grade === 'partial').length
    // Local-fallback results carry no real score — average server scores only.
    const scored = entries.filter((r) => r.source !== 'local-fallback')
    const avgScore = scored.length > 0
      ? Math.round(scored.reduce((sum, r) => sum + r.score, 0) / scored.length)
      : null
    return { pass, warn, reject, partial, total: entries.length, avgScore }
  }, [results])

  // First local-fallback result (if any) — drives the degraded-mode banner
  const fallbackInfo = useMemo(() => {
    const fallback = Object.values(results).find((r) => r.source === 'local-fallback')
    return fallback ? { reason: fallback.fallbackReason ?? 'request failed' } : null
  }, [results])

  // Available stages across all results
  const availableStages = useMemo(() => {
    const stages = new Set<number>()
    for (const r of Object.values(results)) {
      for (const issue of r.issues) stages.add(issue.stage)
    }
    return Array.from(stages).sort((a, b) => a - b)
  }, [results])

  const validateAll = useCallback(async () => {
    if (!currentProject) return

    setState('running')
    setError(null)
    const newResults: Record<string, BlockValidateResponse> = {}

    try {
      const app = currentProject as LoadedApp
      const blockEntries = Object.entries(app.blocks ?? {})

      // Build siblingBlocks map for cross-block checks.
      // ui.root lives under blockDef.ui; flow must be the PER-BLOCK local
      // FlowMap — the server resolves event→flow references against
      // bundle.flow only, and namespaced `app.flows` keys never match.
      const siblingBlocks: Record<string, BlockBundle> = {}
      for (const [blockName, blockDef] of blockEntries) {
        siblingBlocks[blockName] = {
          block: { name: blockName, schemaVersion: (blockDef as any).schemaVersion, blockType: (blockDef as any).blockType },
          ui: { root: (blockDef as any).ui?.root ?? (blockDef as any).root },
          events: (blockDef as any).events ?? {},
          flow: (blockDef as any).flow ?? {},
          variables: (blockDef as any).variables ?? {},
          datasource: (blockDef as any).datasource ?? {},
        }
      }

      for (const [blockName, blockDef] of blockEntries) {
        const bundle: BlockBundle = {
          block: { name: blockName, schemaVersion: (blockDef as any).schemaVersion, blockType: (blockDef as any).blockType },
          ui: { root: (blockDef as any).ui?.root ?? (blockDef as any).root },
          events: (blockDef as any).events ?? {},
          flow: (blockDef as any).flow ?? {},
          variables: (blockDef as any).variables ?? {},
          datasource: (blockDef as any).datasource ?? {},
        }

        try {
          const result = await blockApi.validateBlock(bundle, siblingBlocks)
          newResults[blockName] = result
        } catch (err) {
          newResults[blockName] = {
            valid: false,
            score: 0,
            grade: 'reject',
            errors: [err instanceof Error ? err.message : 'Validation failed'],
            warnings: [],
            issues: [],
          }
        }

        // Update results progressively
        setResults({ ...newResults })
      }

      setState('done')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Validation failed')
      setState('error')
    }
  }, [currentProject])

  // Filter issues
  const filterIssues = useCallback((issues: BlockValidationIssue[]): BlockValidationIssue[] => {
    return issues.filter((issue) => {
      if (levelFilter !== 'all' && issue.level !== levelFilter) return false
      if (stageFilter !== null && issue.stage !== stageFilter) return false
      return true
    })
  }, [levelFilter, stageFilter])

  if (!currentProject) {
    return (
      <div className="h-full flex items-center justify-center text-xs text-muted-foreground">
        No project loaded
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header with score badge */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
        {/* Score badge */}
        {summary && (
          <div className={`flex items-center gap-1.5 px-2 py-1 rounded ${scoreBgColor(summary.avgScore)}`}>
            <span className={`text-sm font-bold font-mono ${scoreColor(summary.avgScore)}`}>
              {summary.avgScore ?? '—'}
            </span>
          </div>
        )}

        {/* Counts */}
        {summary && (
          <span className="text-xs text-muted-foreground">
            <span className="text-success">{summary.pass}P</span>
            {' / '}
            <span className="text-yellow-400">{summary.warn}W</span>
            {' / '}
            <span className="text-red-400">{summary.reject}R</span>
            {summary.partial > 0 && (
              <>
                {' / '}
                <span className="text-amber-400">{summary.partial} partial</span>
              </>
            )}
          </span>
        )}

        <div className="flex-1" />

        {/* Filters */}
        {summary && (
          <div className="flex items-center gap-1">
            {/* Level filter */}
            {(['all', 'error', 'warning', 'info'] as const).map((lvl) => (
              <button
                key={lvl}
                onClick={() => setLevelFilter(lvl)}
                className={`text-[10px] px-1 py-0.5 rounded ${
                  levelFilter === lvl ? 'bg-accent text-accent-foreground font-medium' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {lvl === 'all' ? 'All' : lvl === 'error' ? 'E' : lvl === 'warning' ? 'W' : 'I'}
              </button>
            ))}
            {/* Stage filter */}
            {availableStages.length > 1 && (
              <select
                value={stageFilter ?? ''}
                onChange={(e) => setStageFilter(e.target.value ? Number(e.target.value) : null)}
                className="text-[10px] bg-transparent border border-border rounded px-1 py-0.5 text-muted-foreground outline-none"
              >
                <option value="">All stages</option>
                {availableStages.map((s) => (
                  <option key={s} value={s}>S{s}</option>
                ))}
              </select>
            )}
          </div>
        )}

        {/* Validate button */}
        <button
          onClick={validateAll}
          disabled={state === 'running'}
          className="text-xs bg-primary text-primary-foreground rounded px-2 py-1 hover:bg-primary/90 disabled:opacity-50"
        >
          {state === 'running' ? 'Validating...' : 'Validate'}
        </button>
      </div>

      {/* Degraded-mode banner — local fallback ran instead of server validation */}
      {fallbackInfo && (
        <div className="px-3 py-1.5 border-b border-border bg-amber-500/10 text-[11px] text-amber-400">
          Partial validation only — full server validation unavailable ({fallbackInfo.reason}).
          Local structural checks ran instead of the full validation.
        </div>
      )}

      {/* Results */}
      <div className="flex-1 overflow-auto">
        {state === 'idle' && (
          <p className="text-xs text-muted-foreground px-3 py-4 text-center">
            Click Validate to check spec compliance
          </p>
        )}

        {error && (
          <p className="text-xs text-red-400 px-3 py-2">{error}</p>
        )}

        {Object.entries(results).map(([blockName, result]) => {
          const filteredIssues = filterIssues(result.issues)

          return (
            <div key={blockName} className="border-b border-border/50">
              {/* Block header */}
              <button
                onClick={() => setExpandedBlock(expandedBlock === blockName ? null : blockName)}
                className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-muted/30 text-left"
              >
                <span className="text-[10px] text-muted-foreground">{expandedBlock === blockName ? '\u25BC' : '\u25B6'}</span>
                <span className="text-xs font-mono flex-1 truncate">{blockName}</span>
                <span className={`text-xs font-mono font-bold ${result.grade === 'partial' ? 'text-muted-foreground' : scoreColor(result.score)}`}>
                  {result.grade === 'partial' ? '—' : result.score}
                </span>
                <span className={`text-[10px] uppercase font-medium px-1.5 py-0.5 rounded ${gradeColor(result.grade)} ${
                  result.grade === 'pass' ? 'bg-success/10' :
                  result.grade === 'warn' ? 'bg-yellow-500/10' :
                  result.grade === 'partial' ? 'bg-amber-500/10' : 'bg-red-500/10'
                }`}>
                  {result.grade}
                </span>
              </button>

              {/* Expanded issues */}
              {expandedBlock === blockName && (
                <div className="px-3 pb-2 space-y-0.5">
                  {filteredIssues.length > 0 ? (
                    filteredIssues.map((issue, i) => (
                      <div key={i} className="flex items-start gap-1.5 text-[11px]">
                        <span className={`${levelColor(issue.level)} shrink-0 mt-0.5`}>
                          {levelIcon(issue.level)}
                        </span>
                        <span className="text-muted-foreground shrink-0">S{issue.stage}</span>
                        <span className="text-foreground/80">{issue.message}</span>
                      </div>
                    ))
                  ) : result.issues.length > 0 ? (
                    <p className="text-[11px] text-muted-foreground">
                      {result.issues.length} issues hidden by filter
                    </p>
                  ) : result.source === 'local-fallback' ? (
                    <p className="text-[11px] text-amber-400">
                      Local structural checks passed — full validation unavailable
                    </p>
                  ) : (
                    <p className="text-[11px] text-success">All checks passed</p>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
